import { useEffect, useRef, useReducer } from 'react';
import { useDebounceFn } from 'ahooks';

import { ItemShortType } from 'application/components/SelectableList/types';
import { FN_OPTIONS } from 'application/pages/constants';
import { useNavigate } from 'application/providers/RouterProvider';

import { PointShortTypeFragment } from '@shared/api/graphql/generated';

import { StreamBindModalActionTypes, StreamBindModalFilterType, UseStreamBindModalControllerProps } from './types';
import { streamBindModalReducer } from './reducers';
import { DEFAULT_STREAM_BIND_MODAL_STATE } from './consts';

const useStreamBindModalController = ({
  points,
  targetStreamId,
  pointFromLocation,
  onFilterExecute,
  setPointInput,
}: UseStreamBindModalControllerProps) => {
  const [state, dispatch] = useReducer(streamBindModalReducer, DEFAULT_STREAM_BIND_MODAL_STATE);

  const pointIdAsSingleResultInSearch = useRef<string | undefined>(undefined);

  const navigate = useNavigate();

  const onExecute = async (filters: StreamBindModalFilterType) => {
    await onFilterExecute?.(filters);
  };

  const { run: debouncedSearchRequest } = useDebounceFn((value: string) => {
    if (pointIdAsSingleResultInSearch.current) {
      const newSelectedPoints: Map<string, ItemShortType> = new Map(state.selectedPoints);
      newSelectedPoints.delete(pointIdAsSingleResultInSearch.current);
      setPointInput(false, { id: pointIdAsSingleResultInSearch.current });
      dispatch({ type: StreamBindModalActionTypes.SET_SELECTED_POINTS, selectedPoints: newSelectedPoints });
    }

    onExecute?.({
      query: value,
    });

    dispatch({ type: StreamBindModalActionTypes.SET_DISABLED_POINTS, disabledPoints: new Map() });
  }, FN_OPTIONS);

  useEffect(() => {
    const newSelectedPoints: Map<string, ItemShortType> = new Map(state.selectedPoints);
    let newDisabledPoints: Map<string, ItemShortType> = new Map(state.disabledPoints);
    const pointsLength = points.length;

    if (pointsLength === 1) {
      pointIdAsSingleResultInSearch.current = points[0].id;
      setPointInput(true, {
        id: points[0].id,
        address: points[0].address,
        device: points[0].device as unknown as string,
        isEnabled: points[0].isEnabled as unknown as string,
        stream: points[0].stream as unknown as string,
      });
    }

    points.forEach((point) => {
      const isEqualCurrentStream = point.stream?.id === targetStreamId;

      const isSelected =
        point.id === pointFromLocation?.pointId ||
        isEqualCurrentStream ||
        state.selectedPoints.has(point.id) ||
        pointsLength === 1;

      const isDisabled = isEqualCurrentStream || state.disabledPoints.has(point.id);

      if (isSelected) {
        newSelectedPoints.set(point.id, { id: point.id });
      } else {
        newSelectedPoints.delete(point.id);
      }

      if (isDisabled) {
        newDisabledPoints.set(point.id, { id: point.id, streamId: point.stream?.id || '' });
      } else {
        newDisabledPoints.delete(point.id);
      }

      if (points.length === 1) {
        newDisabledPoints = new Map();

        if (isEqualCurrentStream) {
          newDisabledPoints.set(point.id, { id: point.id, streamId: point.stream?.id || '' });
        }
      }
    });

    dispatch({ type: StreamBindModalActionTypes.SET_SELECTED_POINTS, selectedPoints: newSelectedPoints });
    dispatch({ type: StreamBindModalActionTypes.SET_DISABLED_POINTS, disabledPoints: newDisabledPoints });
  }, [points, targetStreamId, pointFromLocation]);

  const onInitialSearchChange = async (value: string) => {
    dispatch({ type: StreamBindModalActionTypes.SET_SEARCH_TEXT, searchText: value });
    await onExecute({ query: pointFromLocation?.pointId });
  };

  const onSearchChange = (value: string) => {
    if (value.length === 0 && pointFromLocation?.pointId) {
      navigate('.', { state: null, replace: true });
    }

    dispatch({ type: StreamBindModalActionTypes.SET_SEARCH_TEXT, searchText: value });
    debouncedSearchRequest(value);
  };

  const onPointChange = (value: boolean, point: PointShortTypeFragment) => {
    const newSelectedPoints = new Map(state.selectedPoints);

    if (value) {
      newSelectedPoints.set(point.id, { id: point.id });
    } else {
      newSelectedPoints.delete(point.id);
    }

    dispatch({ type: StreamBindModalActionTypes.SET_SELECTED_POINTS, selectedPoints: newSelectedPoints });
    setPointInput(value, {
      id: point.id,
      address: point.address,
      device: point.device as unknown as string,
      isEnabled: point.isEnabled as unknown as string,
      stream: point.stream as unknown as string,
    });
    pointIdAsSingleResultInSearch.current = undefined;
  };

  const setIsStreamBindButtonLoading = (value: boolean) => {
    dispatch({ type: StreamBindModalActionTypes.SET_IS_BIND_LOADING, isBindLoading: value });
  };

  return {
    selectedPoints: state.selectedPoints,
    disabledPoints: state.disabledPoints,
    searchText: state.searchText,
    isStreamBindButtonLoading: state.isBindLoading,
    setIsStreamBindButtonLoading,
    onInitialSearchChange,
    onSearchChange,
    onPointChange,
  };
};

export default useStreamBindModalController;
